// src/pages/StatsPage.tsx
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cardClass } from '../components/Layout';
import PageWrapper from '../components/PageWrapper';
import PageLayout from '../components/PageLayout';
import { useCurrency } from '../contexts/CurrencyContext';
import { useDashboardStats, DashboardOrder } from '../hooks/useDashboardStats';
import { usePlanRestrictions } from '../hooks/usePlanRestrictions';
import { addDays, differenceInCalendarDays, eachDayOfInterval, endOfDay, format, parseISO, startOfDay } from 'date-fns';

import {
  RefreshCw,
  TrendingUp,
  DollarSign,
  Users,
  Clock,
  Target,
  PieChart as PieIcon,
  BarChart3,
} from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';

import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  RadialBarChart,
  RadialBar,
} from 'recharts';

type Period = '7d' | '30d' | '90d' | 'custom' | 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'this_quarter' | 'last_quarter' | 'this_year' | 'last_year';


const COLORS = {
  bgCard: '#0B0E14',
  ring: '#1C2230',
  txtMuted: '#94a3b8',
  txt: '#e5e7eb',
  // palette
  blue: '#3b82f6',
  sky: '#06b6d4',
  green: '#22c55e',
  amber: '#f59e0b',
  purple: '#a78bfa',
  rose: '#f43f5e',
  slate: '#64748b',
};

// Map status categories → colors
const STATUS_COLOR: Record<string, string> = {
  pending: COLORS.amber,
  'in progress': COLORS.blue,
  completed: COLORS.green,
  delivered: COLORS.green,
  paid: COLORS.green,
  done: COLORS.green,
  canceled: COLORS.rose,
};

const COMPLETED_STATUSES = new Set(['completed', 'delivered', 'paid']);

const getEffectiveDate = (order: DashboardOrder) => {
  if (order.completed_date) {
    return parseISO(order.completed_date);
  }
  if (order.created_at) {
    return parseISO(order.created_at);
  }
  return null;
};

const buildDailyRevenueSeries = (orders: DashboardOrder[], rangeStart: Date, rangeEnd: Date) => {
  if (!orders || orders.length === 0) {
    return [];
  }

  const days = eachDayOfInterval({ start: rangeStart, end: rangeEnd });

  return days.map(day => {
    const dayStart = startOfDay(day);
    const dayEnd = endOfDay(day);

    const revenue = orders.reduce((sum, order) => {
      if (!COMPLETED_STATUSES.has(order.status)) return sum;
      const effectiveDate = getEffectiveDate(order);
      if (!effectiveDate) return sum;
      if (effectiveDate >= dayStart && effectiveDate <= dayEnd) {
        return sum + (order.budget || 0);
      }
      return sum;
    }, 0);

    return { date: format(day, 'yyyy-MM-dd'), revenue };
  });
};

const formatStatusLabel = (status: string) => {
  if (status === 'in_progress' || status === 'in progress') {
    return 'In Progress';
  }
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// -------------------- COMPONENT --------------------
const StatsPage: React.FC = () => {
  const navigate = useNavigate();
  const { restrictions, checkAccess } = usePlanRestrictions();
  
  // Protection supplémentaire : rediriger si pas d'accès
  useEffect(() => {
    if (!restrictions?.isAdmin && !checkAccess('stats')) {
      navigate('/dashboard', { replace: true });
    }
  }, [restrictions, checkAccess, navigate]);
  
  if (!restrictions?.isAdmin && !checkAccess('stats')) {
    return null; // Ne rien afficher pendant la redirection
  }
  const { currency } = useCurrency();
  const { stats, loading: statsLoading, error: statsError, refetch } = useDashboardStats();

  const [period, setPeriod] = useState<Period>('30d');
  const [customStart, setCustomStart] = useState<string>(''); // YYYY-MM-DD
  const [customEnd, setCustomEnd] = useState<string>(''); // YYYY-MM-DD
  const [appliedCustom, setAppliedCustom] = useState<{ start: string; end: string } | null>(null);
  const [isRevenueTooltipActive, setRevenueTooltipActive] = useState(false);
  const [focusedRevenueSeries, setFocusedRevenueSeries] = useState<'current' | 'previous' | null>(null);
  const [chartAnimationEnabled, setChartAnimationEnabled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });
  const handlePeriodChange = (next: Period) => {
    setPeriod(next);
    if (next !== 'custom') {
      setAppliedCustom(null);
    }
  };

  const handleApplyCustomRange = () => {
    if (!customStart) return;
    const endVal = customEnd || customStart;
    const startDate = new Date(`${customStart}T00:00:00`);
    const endDate = new Date(`${endVal}T00:00:00`);
    if (startDate > endDate) return;
    setAppliedCustom({ start: customStart, end: endVal });
  };

  useEffect(() => {
    const timer = setTimeout(() => setChartAnimationEnabled(true), 120);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const update = () => setIsDarkMode(root.classList.contains('dark'));
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const revenueFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }),
    [currency]
  );

  const selectedRange = useMemo(() => {
    const today = new Date();
    const normalizedEnd = endOfDay(today);
    let start = startOfDay(today);
    let end = normalizedEnd;

    const resolveCustomRange = (startValue: string, endValue?: string) => {
      const parsedStart = startOfDay(new Date(`${startValue}T00:00:00`));
      const parsedEnd = endOfDay(new Date(`${(endValue || startValue)}T00:00:00`));
      return { start: parsedStart, end: parsedEnd };
    };

    if (period === 'custom' && (appliedCustom?.start || customStart)) {
      const startValue = appliedCustom?.start || customStart;
      const endValue = appliedCustom?.end || customEnd || appliedCustom?.start || customStart;
      if (startValue) {
        return resolveCustomRange(startValue, endValue);
      }
    } else {
      switch (period) {
        case 'today': {
          start = startOfDay(today);
          end = normalizedEnd;
          break;
        }
        case 'yesterday': {
          const day = addDays(startOfDay(today), -1);
          start = day;
          end = endOfDay(day);
          break;
        }
        case '7d': {
          start = startOfDay(addDays(today, -6));
          end = normalizedEnd;
          break;
        }
        case '30d': {
          start = startOfDay(addDays(today, -29));
          end = normalizedEnd;
          break;
        }
        case '90d': {
          start = startOfDay(addDays(today, -89));
          end = normalizedEnd;
          break;
        }
        case 'this_week': {
          const day = new Date(today);
          const diff = day.getDay();
          start = startOfDay(addDays(day, -diff));
          end = normalizedEnd;
          break;
        }
        case 'last_week': {
          const day = new Date(today);
          const diff = day.getDay();
          const startWeek = startOfDay(addDays(day, -diff - 7));
          start = startWeek;
          end = endOfDay(addDays(startWeek, 6));
          break;
        }
        case 'this_month': {
          start = startOfDay(new Date(today.getFullYear(), today.getMonth(), 1));
          end = normalizedEnd;
          break;
        }
        case 'last_month': {
          start = startOfDay(new Date(today.getFullYear(), today.getMonth() - 1, 1));
          end = endOfDay(new Date(today.getFullYear(), today.getMonth(), 0));
          break;
        }
        case 'this_quarter': {
          const quarter = Math.floor(today.getMonth() / 3);
          start = startOfDay(new Date(today.getFullYear(), quarter * 3, 1));
          end = normalizedEnd;
          break;
        }
        case 'last_quarter': {
          const quarter = Math.floor(today.getMonth() / 3) - 1;
          const year = quarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
          const month = quarter < 0 ? 9 : quarter * 3;
          start = startOfDay(new Date(year, month, 1));
          end = endOfDay(new Date(year, month + 3, 0));
          break;
        }
        case 'this_year': {
          start = startOfDay(new Date(today.getFullYear(), 0, 1));
          end = normalizedEnd;
          break;
        }
        case 'last_year': {
          start = startOfDay(new Date(today.getFullYear() - 1, 0, 1));
          end = endOfDay(new Date(today.getFullYear() - 1, 11, 31));
          break;
        }
        default: {
          start = startOfDay(addDays(today, -29));
          end = normalizedEnd;
        }
      }
    }

    return { start, end };
  }, [period, appliedCustom, customStart, customEnd]);

  const previousRange = useMemo(() => {
    const dayCount = Math.max(1, differenceInCalendarDays(selectedRange.end, selectedRange.start) + 1);
    const prevEnd = endOfDay(addDays(selectedRange.start, -1));
    const prevStart = startOfDay(addDays(prevEnd, -(dayCount - 1)));
    return { start: prevStart, end: prevEnd, dayCount };
  }, [selectedRange]);

  const rangeDisplayLabel = useMemo(() => {
    const startLabel = format(selectedRange.start, 'MMM d, yyyy');
    const endLabel = format(selectedRange.end, 'MMM d, yyyy');
    if (startLabel === endLabel) {
      return startLabel;
    }
    return `${startLabel} → ${endLabel}`;
  }, [selectedRange]);

  const computedStats = useMemo(() => {
    if (!stats) {
      return {
        totalRevenue: 0,
        pendingRevenue: 0,
        averageDeliveryTime: 0,
        completionRate: 0,
        averageOrderValue: 0,
        orderVolume: 0,
        pendingOrdersCount: 0,
        pieData: [] as { name: string; value: number }[],
        statusDailyData: [] as { status: string; originalStatus: string; count: number }[],
        stackedData: [] as Record<string, number | string>[],
        topClients: [] as { name: string; revenue: number; orders: number }[],
        recentActivities: [] as { label: string; created_at: string }[],
        currentDailyRevenue: [] as { date: string; revenue: number }[],
        previousDailyRevenue: [] as { date: string; revenue: number }[],
      };
    }

    const { start, end } = selectedRange;
    const filteredOrders = stats.orders.filter(order => {
      if (!order.created_at) return false;
      const createdDate = parseISO(order.created_at);
      return createdDate >= start && createdDate <= end;
    });

    const filteredCompleted = filteredOrders.filter(order => COMPLETED_STATUSES.has(order.status));
    const pendingOrders = filteredOrders.filter(order => !COMPLETED_STATUSES.has(order.status));
    const sumBudget = (orders: DashboardOrder[]) => orders.reduce((sum, order) => sum + (order.budget || 0), 0);

    const totalRevenue = sumBudget(filteredCompleted);
    const pendingRevenue = sumBudget(pendingOrders);
    const orderVolume = filteredOrders.length;
    const completionRate = orderVolume > 0 ? Math.round((filteredCompleted.length / orderVolume) * 100) : 0;
    const averageOrderValue = filteredCompleted.length > 0 ? Math.round(totalRevenue / filteredCompleted.length) : 0;
    const pendingOrdersCount = pendingOrders.length;

    const deliveryTimes = filteredCompleted
      .map(order => {
        const startDate = order.start_date ? parseISO(order.start_date) : parseISO(order.created_at);
        const completedDate = order.completed_date ? parseISO(order.completed_date) : null;
        if (!startDate || !completedDate) return null;
        return (completedDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
      })
      .filter((value): value is number => value !== null);

    const averageDeliveryTime =
      deliveryTimes.length > 0
        ? Number((deliveryTimes.reduce((sum, value) => sum + value, 0) / deliveryTimes.length).toFixed(1))
        : 0;

    const platformRevenue: Record<string, number> = {};
    filteredCompleted.forEach(order => {
      const key = order.platform || 'Other';
      platformRevenue[key] = (platformRevenue[key] || 0) + (order.budget || 0);
    });
    const pieData = Object.entries(platformRevenue)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const statusCounts: Record<string, number> = {};
    filteredOrders.forEach(order => {
      statusCounts[order.status] = (statusCounts[order.status] || 0) + 1;
    });
    const statusDailyData = Object.entries(statusCounts).map(([status, count]) => ({
      status: formatStatusLabel(status),
      originalStatus: status,
      count,
    }));
    const stackedData = statusDailyData.map(s => {
      return {
        status: s.status,
        [s.originalStatus]: s.count,
      };
    });

    const clientRevenue: Record<string, { revenue: number; orders: number }> = {};
    filteredOrders.forEach(order => {
      const clientName = order.client_name || 'Unknown';
      if (!clientRevenue[clientName]) {
        clientRevenue[clientName] = { revenue: 0, orders: 0 };
      }
      clientRevenue[clientName].revenue += order.budget || 0;
      clientRevenue[clientName].orders += 1;
    });
    const topClients = Object.entries(clientRevenue)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    const recentActivities = filteredOrders
      .slice()
      .sort((a, b) => parseISO(b.created_at).getTime() - parseISO(a.created_at).getTime())
      .slice(0, 5)
      .map(order => ({
        label: order.title,
        created_at: order.created_at,
      }));

    const currentDailyRevenue = buildDailyRevenueSeries(stats.orders, selectedRange.start, selectedRange.end);
    const previousDailyRevenue =
      previousRange.dayCount > 0
        ? buildDailyRevenueSeries(stats.orders, previousRange.start, previousRange.end)
        : [];

    return {
      totalRevenue,
      pendingRevenue,
      averageDeliveryTime,
      completionRate,
      averageOrderValue,
      orderVolume,
      pendingOrdersCount,
      pieData,
      statusDailyData,
      stackedData,
      topClients,
      recentActivities,
      currentDailyRevenue,
      previousDailyRevenue,
    };
  }, [stats, selectedRange, previousRange]);

  const {
    totalRevenue,
    pendingRevenue,
    averageDeliveryTime,
    completionRate,
    averageOrderValue,
    orderVolume,
    pendingOrdersCount,
    pieData,
    statusDailyData,
    stackedData,
    topClients,
    recentActivities,
    currentDailyRevenue,
    previousDailyRevenue,
  } = computedStats;
  
  const formattedCurrentRevenue = useMemo(
    () =>
      currentDailyRevenue.map(entry => {
        const parsed = parseISO(entry.date);
        return {
          date: format(parsed, 'MMM d'),
          rawDate: entry.date,
          revenue: entry.revenue,
        };
      }),
    [currentDailyRevenue]
  );

  const formattedComparisonRevenue = useMemo(
    () =>
      previousDailyRevenue.map(entry => {
        const parsed = parseISO(entry.date);
        return {
          date: format(parsed, 'MMM d'),
          rawDate: entry.date,
          revenue: entry.revenue,
        };
      }),
    [previousDailyRevenue]
  );

  const combinedRevenueChartData = formattedCurrentRevenue.map((row, index) => {
    const comparisonRow = formattedComparisonRevenue[index];
    const previousRevenue = comparisonRow ? comparisonRow.revenue : null;
    const delta = previousRevenue !== null ? row.revenue - previousRevenue : null;
    const deltaPercent =
      previousRevenue && previousRevenue !== 0 ? ((row.revenue - previousRevenue) / previousRevenue) * 100 : null;

    return {
      date: row.date,
      rawDate: row.rawDate,
      currentRevenue: row.revenue,
      previousRevenue: previousRevenue ?? 0,
      previousLabel: comparisonRow ? comparisonRow.date : null,
      delta,
      deltaPercent,
    };
  });

  const areaCumulativeData = formattedCurrentRevenue.map((row, index) => {
    const cumulative = formattedCurrentRevenue.slice(0, index + 1).reduce((sum, r) => sum + r.revenue, 0);
    return { date: row.date, cumulative };
  });

  const revenueChartData = combinedRevenueChartData;

  const primaryStrokeColor = isDarkMode ? '#9c68f2' : '#7d3df0';
  const primaryAreaBaseOpacity = isDarkMode ? 0.16 : 0.08;
  const primaryAreaActiveOpacity = isDarkMode ? 0.24 : 0.14;
  const primaryDimmedOpacity = isDarkMode ? 0.12 : 0.08;
  const comparisonStrokeColor = isDarkMode ? '#b896ff' : '#8a6dff';
  const comparisonDimmedOpacity = isDarkMode ? 0.12 : 0.08;
  const comparisonDotFill = isDarkMode ? '#0f172a' : '#ffffff';

  const revenueGradientId = 'statsRevenueGradient';
  const isCurrentDimmed = focusedRevenueSeries === 'previous';
  const isPreviousDimmed = focusedRevenueSeries === 'current';

  const handleRevenueChartMouseMove = (state: any) => {
    if (state?.isTooltipActive) {
      if (!isRevenueTooltipActive) {
        setRevenueTooltipActive(true);
      }
      const payloads: any[] | undefined = state?.activePayload;
      const pointerY: number | undefined = state?.chartY;
      const yAxisMap: Record<string, any> | undefined = state?.yAxisMap;
      if (payloads && payloads.length > 0 && typeof pointerY === 'number' && yAxisMap) {
        let closestSeries: 'current' | 'previous' | null = null;
        let closestDelta = Number.POSITIVE_INFINITY;
        payloads.forEach(entry => {
          const value = typeof entry.value === 'number' ? entry.value : null;
          const axisId = entry?.yAxisId ?? '0';
          const scaleFn = yAxisMap[axisId]?.scale;
          if (value !== null && typeof scaleFn === 'function') {
            const pointY = scaleFn(value);
            if (typeof pointY === 'number') {
              const diff = Math.abs(pointerY - pointY);
              if (diff < closestDelta) {
                closestDelta = diff;
                closestSeries = entry.dataKey === 'previousRevenue' ? 'previous' : 'current';
              }
            }
          }
        });
        if (closestSeries !== focusedRevenueSeries) {
          setFocusedRevenueSeries(closestSeries);
        }
      } else if (focusedRevenueSeries !== null) {
        setFocusedRevenueSeries(null);
      }
    } else if (isRevenueTooltipActive) {
      setRevenueTooltipActive(false);
      if (focusedRevenueSeries !== null) {
        setFocusedRevenueSeries(null);
      }
    }
  };

  const handleRevenueChartMouseLeave = () => {
    if (isRevenueTooltipActive) {
      setRevenueTooltipActive(false);
    }
    if (focusedRevenueSeries !== null) {
      setFocusedRevenueSeries(null);
    }
  };

  const renderRevenueTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    const currentEntry = payload.find((entry: any) => entry.dataKey === 'currentRevenue');
    const previousEntry = payload.find((entry: any) => entry.dataKey === 'previousRevenue');
    const { previousLabel, delta, deltaPercent } = currentEntry?.payload || {};
    const currentValue = currentEntry?.value ?? 0;
    const previousValue = previousEntry?.value ?? 0;
    const deltaValue = typeof delta === 'number' ? delta : null;
    const deltaPercentValue = typeof deltaPercent === 'number' ? deltaPercent : null;
    const deltaSign = deltaValue !== null ? (deltaValue >= 0 ? '+' : '-') : '';
    const deltaPercentSign = deltaPercentValue !== null ? (deltaPercentValue >= 0 ? '+' : '-') : '';
    const deltaFormatted =
      deltaValue !== null ? revenueFormatter.format(Math.abs(deltaValue)) : null;
    const deltaPercentFormatted =
      deltaPercentValue !== null ? `${Math.abs(deltaPercentValue).toFixed(1)}%` : null;

    return (
      <div className="w-[220px] rounded-lg border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur-sm dark:border-slate-700 dark:bg-slate-900/95">
        <div className="text-xs font-medium text-slate-500 dark:text-slate-300">{label}</div>
        <div className="mt-2 space-y-1.5 text-sm">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 font-medium text-slate-700 dark:text-slate-100">
              <span
                className="inline-flex h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: primaryStrokeColor }}
              />
              Current
            </span>
            <span className="font-semibold text-slate-900 dark:text-white">
              {revenueFormatter.format(currentValue)}
            </span>
          </div>
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-300">
            <span className="flex items-center gap-2">
              <span
                className="inline-flex h-2.5 w-2.5 rounded-full border border-dashed"
                style={{ borderColor: comparisonStrokeColor }}
              />
              Previous{previousLabel ? ` (${previousLabel})` : ''}
            </span>
            <span>{revenueFormatter.format(previousValue)}</span>
          </div>
        </div>
        {deltaFormatted && (
          <div className="mt-3 space-y-1 border-t border-slate-200 pt-2 text-xs dark:border-slate-700">
            <div className="flex items-center justify-between text-slate-600 dark:text-slate-300">
              <span>Change</span>
              <span className={`font-semibold ${deltaValue! >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                {deltaSign}
                {deltaFormatted}
              </span>
            </div>
            {deltaPercentFormatted && (
              <div className="flex items-center justify-between text-slate-500 dark:text-slate-300">
                <span>vs. previous</span>
                <span className={`font-semibold ${deltaValue! >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {deltaPercentSign}
                  {deltaPercentFormatted}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // -------------------- ACCESS --------------------
  // Protection gérée par SubscriptionGuard dans App.tsx
  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <p className="ml-3 text-[rgb(var(--muted))]">{'Loading...'}</p>
        </div>
    );
  }

  if (statsError) {
    return (
      <div className="p-6 text-center">
          <p className="text-red-400 font-semibold">Error loading statistics</p>
          <p className="text-sm theme-text-secondary mt-1">{statsError}</p>
          <button
            onClick={refetch}
            className="mt-4 px-4 py-2 bg-blue-600 text-[rgb(var(--fg))] rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6 text-center">
          <p className="text-[rgb(var(--muted))]">No statistics available</p>
        </div>
    );
  }

  // -------------------- RENDER --------------------
  return (
    <PageWrapper>
      <PageLayout
        title="Analytics & Insights"
        actions={
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center lg:justify-end">
            <div className="w-full sm:w-auto max-w-xs">
              <DateRangePicker
                period={period}
                onPeriodChange={handlePeriodChange}
                customStart={customStart}
                customEnd={customEnd}
                onCustomStartChange={setCustomStart}
                onCustomEndChange={setCustomEnd}
                onApply={handleApplyCustomRange}
              />
            </div>
            <button
              onClick={refetch}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] dark:border-white/20 dark:bg-white dark:text-gray-900 dark:hover:bg-white/90"
            >
              <RefreshCw className={`${statsLoading ? 'animate-spin' : ''}`} size={16} />
              {'Refresh'}
            </button>
          </div>
        }
      >
        {/* KPI CARDS - Style Shopify */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className={`${cardClass} p-5`}>
            <p className="text-xs font-medium theme-text-tertiary uppercase tracking-wide mb-2">{'Total Revenue'}</p>
            <p className="text-2xl font-semibold theme-text-primary mb-1">{new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(totalRevenue)}</p>
            <p className="text-xs theme-text-secondary">{'Avg. order value:'} {new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(averageOrderValue)}</p>
          </div>

          <div className={`${cardClass} p-5`}>
            <p className="text-xs font-medium theme-text-tertiary uppercase tracking-wide mb-2">{'Pending Revenue'}</p>
            <p className="text-2xl font-semibold theme-text-primary mb-1">{new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(pendingRevenue)}</p>
            <p className="text-xs theme-text-secondary">{'Open orders:'} {pendingOrdersCount}</p>
          </div>

          <div className={`${cardClass} p-5`}>
            <p className="text-xs font-medium theme-text-tertiary uppercase tracking-wide mb-2">{'Avg. Delivery Time'}</p>
            <p className="text-2xl font-semibold theme-text-primary mb-1">{averageDeliveryTime.toFixed(1)} {'days'}</p>
            <p className="text-xs theme-text-secondary">{'Based on completed orders'}</p>
          </div>

          <div className={`${cardClass} p-5`}>
            <p className="text-xs font-medium theme-text-tertiary uppercase tracking-wide mb-2">{'Completion Rate'}</p>
            <p className="text-2xl font-semibold theme-text-primary mb-1">{completionRate}%</p>
            <p className="text-xs theme-text-secondary">{'Total orders:'} {orderVolume}</p>
          </div>
        </div>

        {/* PRIMARY REVENUE CHART */}
        <div className={`${cardClass} mt-4 p-5`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="text-gray-900 dark:text-[rgb(var(--fg))]" size={18} />
              <h2 className="text-lg font-semibold theme-text-primary">{'Revenue Momentum'}</h2>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <span className="flex items-center gap-2">
                <span className="inline-flex h-2.5 w-2.5 rounded-full bg-[#9c68f2]" />
                {'Current period'}
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-flex h-0.5 w-4 border-b-2 border-dashed border-[#b896ff]" />
                {'Previous period'}
              </span>
            </div>
          </div>
          <div className="mt-4 h-[360px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={revenueChartData}
                onMouseMove={handleRevenueChartMouseMove}
                onMouseLeave={handleRevenueChartMouseLeave}
              >
                <defs>
                  <linearGradient id={revenueGradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={primaryStrokeColor}
                      stopOpacity={isCurrentDimmed ? primaryDimmedOpacity : isRevenueTooltipActive ? primaryAreaActiveOpacity : primaryAreaBaseOpacity}
                    />
                    <stop offset="95%" stopColor={primaryStrokeColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(148, 163, 184, 0.18)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} width={72} />
                <Tooltip cursor={{ stroke: primaryStrokeColor, strokeOpacity: 0.2 }} content={renderRevenueTooltip} />
                <Area
                  type="monotone"
                  dataKey="currentRevenue"
                  stroke={primaryStrokeColor}
                  strokeWidth={0}
                  fill={`url(#${revenueGradientId})`}
                  fillOpacity={isCurrentDimmed ? primaryDimmedOpacity : 1}
                  isAnimationActive={chartAnimationEnabled}
                  animationDuration={700}
                />
                <Line
                  type="monotone"
                  dataKey="currentRevenue"
                  stroke={primaryStrokeColor}
                  strokeWidth={isCurrentDimmed ? 2.2 : 2.8}
                  strokeOpacity={isCurrentDimmed ? primaryDimmedOpacity : 1}
                  dot={false}
                  activeDot={
                    isCurrentDimmed
                      ? false
                      : { r: 6, fill: primaryStrokeColor, stroke: '#ffffff', strokeWidth: 2 }
                  }
                  isAnimationActive={chartAnimationEnabled}
                  animationDuration={760}
                />
                <Line
                  type="monotone"
                  dataKey="previousRevenue"
                  stroke={comparisonStrokeColor}
                  strokeWidth={isPreviousDimmed ? 2.2 : 2.6}
                  strokeOpacity={isPreviousDimmed ? comparisonDimmedOpacity : 0.85}
                  strokeDasharray="6 8"
                  dot={
                    isPreviousDimmed
                      ? false
                      : { r: 4, fill: comparisonDotFill, stroke: comparisonStrokeColor, strokeWidth: 2 }
                  }
                  activeDot={
                    isPreviousDimmed
                      ? false
                      : { r: 5, fill: comparisonStrokeColor, stroke: '#ffffff', strokeWidth: 2 }
                  }
                  connectNulls
                  isAnimationActive={chartAnimationEnabled}
                  animationDuration={760}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* CHARTS ROW 1: Revenue (Bar) + Cumulative (Line) */}
        <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className={`${cardClass} p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="text-gray-900 dark:text-[rgb(var(--fg))]" size={18} />
              <h2 className="text-lg font-semibold theme-text-primary">{'Daily Revenue'}</h2>
            </div>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={formattedCurrentRevenue}>
                  <CartesianGrid stroke="#e5e7eb" strokeDasharray="0" opacity={0.1} />
                  <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ 
                      background: '#ffffff', 
                      border: '1px solid #e5e7eb', 
                      color: '#1a1a1a',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}
                    labelStyle={{ color: '#6b7280', fontSize: '12px' }}
                    itemStyle={{ color: '#1a1a1a', fontSize: '14px' }}
                  />
                  <Bar dataKey="revenue" fill={COLORS.sky} radius={[4, 4, 0, 0]} stroke={COLORS.sky} strokeWidth={2} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={`${cardClass} p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="text-gray-900 dark:text-[rgb(var(--fg))]" size={18} />
              <h2 className="text-lg font-semibold theme-text-primary">{'Cumulative Revenue'}</h2>
            </div>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={areaCumulativeData}>
                  <CartesianGrid stroke="#e5e7eb" strokeDasharray="0" opacity={0.1} />
                  <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ 
                      background: '#ffffff', 
                      border: '1px solid #e5e7eb', 
                      color: '#1a1a1a',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}
                    labelStyle={{ color: '#6b7280', fontSize: '12px' }}
                    itemStyle={{ color: '#1a1a1a', fontSize: '14px' }}
                  />
                  <Line type="monotone" dataKey="cumulative" stroke={COLORS.green} fill={COLORS.green} strokeWidth={3} dot={{ fill: COLORS.green, r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* CHARTS ROW 2: Earnings Breakdown (Pie) + Status Stacked Bars */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className={`${cardClass} p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <PieIcon className="text-gray-900 dark:text-[rgb(var(--fg))]" size={18} />
              <h2 className="text-lg font-semibold theme-text-primary">{'Earnings by Platform'}</h2>
            </div>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip
                    contentStyle={{ 
                      background: '#ffffff', 
                      border: '1px solid #e5e7eb', 
                      color: '#1a1a1a',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}
                    labelStyle={{ color: '#6b7280', fontSize: '12px' }}
                    itemStyle={{ color: '#1a1a1a', fontSize: '14px' }}
                  />
                  <Legend verticalAlign="bottom" height={24} wrapperStyle={{ color: '#6b7280', fontSize: 12 }} />
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={90}
                    innerRadius={50}
                    paddingAngle={2}
                  >
                    {pieData.map((_entry, index) => {
                      const palette = [COLORS.sky, COLORS.green, COLORS.purple, COLORS.amber, COLORS.rose, COLORS.blue, COLORS.slate];
                      return <Cell key={`cell-${index}`} fill={palette[index % palette.length]} />;
                    })}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={`${cardClass} p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="text-gray-900 dark:text-[rgb(var(--fg))]" size={18} />
              <h2 className="text-lg font-semibold theme-text-primary">{'Orders by Status (Daily)'}</h2>
            </div>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stackedData} stackOffset="expand">
                  <CartesianGrid stroke="#e5e7eb" strokeDasharray="0" opacity={0.1} />
                  <XAxis 
                    dataKey="status" 
                    tick={{ fill: '#6b7280', fontSize: 12 }}
                    tickFormatter={(value) => formatStatusLabel(value)}
                  />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ 
                      background: '#ffffff', 
                      border: '1px solid #e5e7eb', 
                      color: '#1a1a1a',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}
                    labelStyle={{ color: '#6b7280', fontSize: '12px' }}
                    itemStyle={{ color: '#1a1a1a', fontSize: '14px' }}
                    formatter={(value: any, name: string) => {
                      const formattedStatus = formatStatusLabel(name);
                      return [value, formattedStatus];
                    }}
                  />
                  {statusDailyData.map((s) => {
                    // Utiliser la couleur grise pour "in_progress" comme sur dashboard
                    let c = STATUS_COLOR[s.originalStatus] || COLORS.slate;
                    if (s.originalStatus === 'in_progress') {
                      c = COLORS.slate; // Gris comme sur dashboard
                    }
                    return <Bar key={s.originalStatus} dataKey={s.originalStatus} stackId="s" fill={c} />;
                  })}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ADDITIONAL STATISTICS ROW */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* Client Growth Trend */}
          <div className={`${cardClass} p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <Users className="text-gray-900 dark:text-[rgb(var(--fg))]" size={18} />
              <h2 className="text-lg font-semibold theme-text-primary">{'Client Growth Trend'}</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm theme-text-secondary">New Clients This Month</span>
                <span className="text-lg font-semibold theme-text-primary">{stats?.totalClients || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm theme-text-secondary">Growth Rate</span>
                <span className="text-lg font-semibold text-green-600">+{stats?.clientsGrowth || 0}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm theme-text-secondary">Retention Rate</span>
                <span className="text-lg font-semibold text-[rgb(var(--brand))]">{completionRate}%</span>
              </div>
            </div>
          </div>

          {/* Order Performance */}
          <div className={`${cardClass} p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <Target className="text-gray-900 dark:text-[rgb(var(--fg))]" size={18} />
              <h2 className="text-lg font-semibold theme-text-primary">{'Order Performance'}</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm theme-text-secondary">Total Orders</span>
                <span className="text-lg font-semibold theme-text-primary">{orderVolume}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm theme-text-secondary">Avg. Order Value</span>
                <span className="text-lg font-semibold theme-text-primary">{new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(averageOrderValue)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm theme-text-secondary">Pending Orders</span>
                <span className="text-lg font-semibold text-amber-600">{pendingOrdersCount}</span>
              </div>
            </div>
          </div>

          {/* Revenue Breakdown */}
          <div className={`${cardClass} p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="text-gray-900 dark:text-[rgb(var(--fg))]" size={18} />
              <h2 className="text-lg font-semibold theme-text-primary">{'Revenue Breakdown'}</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm theme-text-secondary">Total Revenue</span>
                <span className="text-lg font-semibold theme-text-primary">{new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(totalRevenue)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm theme-text-secondary">Pending Revenue</span>
                <span className="text-lg font-semibold theme-text-primary">{new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(pendingRevenue)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm theme-text-secondary">Revenue Growth</span>
                <span className="text-lg font-semibold text-green-600">+{stats?.revenueGrowth || 0}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* PERFORMANCE METRICS ROW */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* Delivery Performance */}
          <div className={`${cardClass} p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="text-gray-900 dark:text-[rgb(var(--fg))]" size={18} />
              <h2 className="text-lg font-semibold theme-text-primary">{'Delivery Performance'}</h2>
            </div>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart
                  innerRadius="60%"
                  outerRadius="90%"
                  data={[{ name: 'Avg Delivery', value: averageDeliveryTime }]}
                  startAngle={180}
                  endAngle={0}
                >
                  <RadialBar dataKey="value" fill="#f59e0b" cornerRadius={8} />
                  <Legend
                    content={() => (
                      <div className="text-center mt-2">
                        <span className="text-2xl font-bold theme-text-primary">{averageDeliveryTime.toFixed(1)}</span>
                        <div className="text-xs theme-text-secondary">Days Average</div>
                      </div>
                    )}
                  />
                </RadialBarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Order Status Distribution */}
          <div className={`${cardClass} p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="text-gray-900 dark:text-[rgb(var(--fg))]" size={18} />
              <h2 className="text-lg font-semibold theme-text-primary">{'Order Status Distribution'}</h2>
            </div>
            <div className="space-y-3">
              {statusDailyData.map((status, index) => {
                const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
                const total = statusDailyData.reduce((sum, item) => sum + item.count, 0);
                const percentage = total > 0 ? Math.round((status.count / total) * 100) : 0;
                // Utiliser la couleur grise pour "in_progress" comme sur dashboard
                let color = STATUS_COLOR[status.originalStatus] || colors[index % colors.length];
                if (status.originalStatus === 'in_progress') {
                  color = COLORS.slate; // Gris comme sur dashboard
                }
                return (
                  <div key={index} className="flex items-center justify-between p-2 rounded-lg bg-white dark:bg-[#303030] hover:bg-gray-50 dark:hover:bg-[#3a3a3a] border border-[rgb(var(--border))] dark:border-[#404040]">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-sm theme-text-primary">{status.status}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold theme-text-primary">{status.count}</span>
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-slate-700 theme-text-secondary">
                        {percentage}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* CARDS: Completion Gauge + Top Clients + Recent Activity */}
        <div className="grid grid-cols-1 2xl:grid-cols-3 gap-4">
          {/* Gauge */}
          <div className={`${cardClass} p-4`}>
            <h2 className="text-lg font-semibold theme-text-primary mb-3">{'Completion Gauge'}</h2>
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart
                  innerRadius="60%"
                  outerRadius="90%"
                  data={[{ name: 'Completion', value: completionRate }]}
                  startAngle={180}
                  endAngle={0}
                >
                  <RadialBar dataKey="value" fill={COLORS.green} cornerRadius={8} />
                  <Legend
                    content={() => (
                      <div className="text-center mt-2">
                        <span className="text-3xl font-bold theme-text-primary">{completionRate}%</span>
                        <div className="text-xs theme-text-secondary">{'Orders completed'}</div>
                      </div>
                    )}
                  />
                </RadialBarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Clients */}
          <div className={`${cardClass} p-4`}>
            <h2 className="text-lg font-semibold theme-text-primary mb-3">{'Top Clients'}</h2>
            <div className="space-y-2">
              {topClients.length === 0 && <p className="text-sm theme-text-tertiary">{'No data.'}</p>}
              {topClients.map((c, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between rounded-lg bg-white dark:bg-[#303030] hover:bg-gray-50 dark:hover:bg-[#3a3a3a] border border-[rgb(var(--border))] dark:border-[#404040] px-3 py-2 transition-colors"
                >
                  <span className="text-sm theme-text-primary">{c.name}</span>
                  <span className="text-sm theme-text-primary font-medium">{new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(c.revenue)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className={`${cardClass} p-4`}>
            <h2 className="text-lg font-semibold theme-text-primary mb-3">{'Recent Activity'}</h2>
            <div className="space-y-2">
              {recentActivities.length === 0 && <p className="text-sm theme-text-tertiary">{'No recent orders.'}</p>}
              {recentActivities.map((a, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between rounded-lg bg-white dark:bg-[#303030] hover:bg-gray-50 dark:hover:bg-[#3a3a3a] border border-[rgb(var(--border))] dark:border-[#404040] px-3 py-2 transition-colors"
                >
                  <span className="text-sm theme-text-primary truncate max-w-[70%]">{a.label}</span>
                  <span className="text-xs theme-text-secondary">{new Date(a.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PageLayout>
    </PageWrapper>
  );
};

export default StatsPage;
